import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import api, { formatError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card } from "../components/ui/card";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Plus, Trash, PencilSimple, CurrencyEur, ChartPieSlice } from "@phosphor-icons/react";
import { toast } from "sonner";

const CATEGORIAS = [
  "Materiales",
  "Herramientas",
  "Transporte",
  "Combustible",
  "Alimentación",
  "Salarios",
  "Impuestos",
  "Oficina",
  "Mantenimiento",
  "Otros",
];

const MESES = ["Ene", "Feb", "Mar", "Abr", "May", "Jun", "Jul", "Ago", "Sep", "Oct", "Nov", "Dic"];

export default function Gastos() {
  const { t } = useTranslation();
  const [items, setItems] = useState([]);
  const [resumen, setResumen] = useState(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const todayISO = new Date().toISOString().split("T")[0];
  const firstOfMonthISO = (() => {
    const d = new Date(); d.setDate(1); return d.toISOString().split("T")[0];
  })();

  const [filters, setFilters] = useState({
    desde: "",
    hasta: "",
    categoria: "todos",
    anio: new Date().getFullYear(),
  });

  const empty = {
    fecha: todayISO,
    descripcion: "",
    categoria: "Materiales",
    monto: 0,
    proveedor: "",
    metodo_pago: "",
    notas: "",
  };
  const [form, setForm] = useState(empty);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.desde) params.desde = filters.desde;
      if (filters.hasta) params.hasta = filters.hasta;
      if (filters.categoria && filters.categoria !== "todos") params.categoria = filters.categoria;
      const [g, r] = await Promise.all([
        api.get("/gastos", { params }),
        api.get("/gastos/resumen", { params: { anio: filters.anio } }),
      ]);
      setItems(g.data);
      setResumen(r.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.desde, filters.hasta, filters.categoria, filters.anio]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form, monto: parseFloat(form.monto) || 0 };
      if (editing) {
        await api.put(`/gastos/${editing}`, payload);
      } else {
        await api.post("/gastos", payload);
      }
      toast.success(t("common.success"));
      setOpen(false);
      setEditing(null);
      setForm(empty);
      load();
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    }
  };

  const openEdit = (g) => {
    setEditing(g.id);
    setForm({
      fecha: g.fecha,
      descripcion: g.descripcion,
      categoria: g.categoria || "Otros",
      monto: g.monto,
      proveedor: g.proveedor || "",
      metodo_pago: g.metodo_pago || "",
      notas: g.notas || "",
    });
    setOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("?")) return;
    await api.delete(`/gastos/${id}`);
    toast.success(t("common.success"));
    load();
  };

  const totalFiltrado = useMemo(() => items.reduce((s, g) => s + (g.monto || 0), 0), [items]);

  const maxMes = useMemo(() => Math.max(...(resumen?.por_mes?.map((m) => m.total) || [1]), 1), [resumen]);

  return (
    <div data-testid="gastos-page">
      <div className="flex items-start justify-between mb-8 flex-wrap gap-3">
        <div>
          <h1 className="text-3xl heading-font font-bold text-slate-900">{t("gastos.title")}</h1>
          <p className="text-slate-500 mt-1 text-sm">{t("gastos.subtitle")}</p>
        </div>

        <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) { setEditing(null); setForm(empty); } }}>
          <DialogTrigger asChild>
            <Button className="bg-green-600 hover:bg-green-700 gap-2" data-testid="add-gasto-btn">
              <Plus size={18} weight="bold" /> {t("gastos.add")}
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>{editing ? t("gastos.edit") : t("gastos.add")}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>{t("gastos.date")}</Label>
                  <Input type="date" value={form.fecha} onChange={(e) => setForm({ ...form, fecha: e.target.value })} required data-testid="gasto-date-input" />
                </div>
                <div>
                  <Label>{t("gastos.amount")}</Label>
                  <Input type="number" step="0.01" value={form.monto} onChange={(e) => setForm({ ...form, monto: e.target.value })} required data-testid="gasto-amount-input" />
                </div>
              </div>
              <div>
                <Label>{t("gastos.description")}</Label>
                <Input value={form.descripcion} onChange={(e) => setForm({ ...form, descripcion: e.target.value })} required data-testid="gasto-desc-input" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>{t("gastos.category")}</Label>
                  <Select value={form.categoria} onValueChange={(v) => setForm({ ...form, categoria: v })}>
                    <SelectTrigger data-testid="gasto-cat-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {CATEGORIAS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>{t("gastos.payment")}</Label>
                  <Input value={form.metodo_pago} onChange={(e) => setForm({ ...form, metodo_pago: e.target.value })} placeholder="Efectivo, Tarjeta..." />
                </div>
              </div>
              <div>
                <Label>{t("gastos.supplier")}</Label>
                <Input value={form.proveedor} onChange={(e) => setForm({ ...form, proveedor: e.target.value })} />
              </div>
              <div>
                <Label>{t("gastos.notes")}</Label>
                <Textarea value={form.notas} onChange={(e) => setForm({ ...form, notas: e.target.value })} rows={2} />
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setOpen(false)}>{t("gastos.cancel")}</Button>
                <Button type="submit" className="bg-green-600 hover:bg-green-700" data-testid="gasto-save-btn">{t("gastos.save")}</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Resumen cards + Monthly chart */}
      {resumen && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <Card className="p-5 border-slate-200 card-hover">
            <div className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1">{t("gastos.total")} {resumen.anio}</div>
            <div className="text-3xl heading-font font-bold text-red-700">€ {resumen.total.toFixed(2)}</div>
            <div className="text-xs text-slate-500 mt-1">{resumen.cantidad} {t("gastos.cantidad")}</div>
          </Card>

          <Card className="p-5 border-slate-200 col-span-2">
            <div className="flex items-center justify-between mb-3">
              <div className="text-xs font-bold uppercase tracking-wider text-slate-500">{t("gastos.byMonth")}</div>
              <Select value={String(filters.anio)} onValueChange={(v) => setFilters({ ...filters, anio: parseInt(v) })}>
                <SelectTrigger className="w-24 h-7 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {[0, 1, 2, 3, 4].map((i) => {
                    const y = new Date().getFullYear() - i;
                    return <SelectItem key={y} value={String(y)}>{y}</SelectItem>;
                  })}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-end gap-1 h-28">
              {resumen.por_mes.map((m) => (
                <div key={m.mes} className="flex-1 flex flex-col items-center gap-1" title={`${MESES[m.mes - 1]}: € ${m.total.toFixed(2)}`}>
                  <div
                    className="w-full bg-gradient-to-t from-red-500 to-red-400 rounded-t"
                    style={{ height: `${(m.total / maxMes) * 90}%`, minHeight: m.total > 0 ? "4px" : "1px" }}
                  />
                  <div className="text-[10px] text-slate-500 font-medium">{MESES[m.mes - 1]}</div>
                </div>
              ))}
            </div>
          </Card>
        </div>
      )}

      {/* Filters */}
      <Card className="p-4 mb-4 border-slate-200">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
          <div>
            <Label>{t("gastos.from")}</Label>
            <Input type="date" value={filters.desde} onChange={(e) => setFilters({ ...filters, desde: e.target.value })} data-testid="gastos-from" />
          </div>
          <div>
            <Label>{t("gastos.to")}</Label>
            <Input type="date" value={filters.hasta} onChange={(e) => setFilters({ ...filters, hasta: e.target.value })} data-testid="gastos-to" />
          </div>
          <div>
            <Label>{t("gastos.category")}</Label>
            <Select value={filters.categoria} onValueChange={(v) => setFilters({ ...filters, categoria: v })}>
              <SelectTrigger data-testid="gastos-cat-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="todos">{t("gastos.allCategories")}</SelectItem>
                {CATEGORIAS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="bg-slate-50 p-3 rounded-lg border border-slate-200">
            <div className="text-[11px] uppercase tracking-wider text-slate-500 font-bold">{t("gastos.totalMonth")}</div>
            <div className="text-xl heading-font font-bold text-red-700">€ {totalFiltrado.toFixed(2)}</div>
          </div>
        </div>
      </Card>

      {/* Table */}
      {loading ? (
        <Card className="p-10 text-center text-slate-400">{t("common.loading")}</Card>
      ) : items.length === 0 ? (
        <Card className="p-12 text-center text-slate-400 border-slate-200">
          <CurrencyEur size={40} weight="duotone" className="mx-auto mb-3 text-slate-300" />
          {t("gastos.empty")}
        </Card>
      ) : (
        <Card className="border-slate-200 overflow-hidden">
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="text-left text-xs font-bold uppercase tracking-wider text-slate-500 px-4 py-3">{t("gastos.date")}</th>
                <th className="text-left text-xs font-bold uppercase tracking-wider text-slate-500 px-4 py-3">{t("gastos.description")}</th>
                <th className="text-left text-xs font-bold uppercase tracking-wider text-slate-500 px-4 py-3">{t("gastos.category")}</th>
                <th className="text-left text-xs font-bold uppercase tracking-wider text-slate-500 px-4 py-3">{t("gastos.supplier")}</th>
                <th className="text-right text-xs font-bold uppercase tracking-wider text-slate-500 px-4 py-3">{t("gastos.amount")}</th>
                <th className="text-right text-xs font-bold uppercase tracking-wider text-slate-500 px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {items.map((g) => (
                <tr key={g.id} className="border-b border-slate-100 hover:bg-slate-50/70" data-testid={`gasto-row-${g.id}`}>
                  <td className="px-4 py-3 text-sm text-slate-700">{g.fecha}</td>
                  <td className="px-4 py-3 text-sm font-medium text-slate-900">{g.descripcion}</td>
                  <td className="px-4 py-3">
                    <Badge className="bg-slate-100 text-slate-700 border-0">{g.categoria}</Badge>
                  </td>
                  <td className="px-4 py-3 text-sm text-slate-600">{g.proveedor || "—"}</td>
                  <td className="px-4 py-3 text-sm text-right font-bold text-red-700">€ {(g.monto || 0).toFixed(2)}</td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    <Button variant="ghost" size="sm" onClick={() => openEdit(g)} data-testid={`edit-gasto-${g.id}`}>
                      <PencilSimple size={16} />
                    </Button>
                    <Button variant="ghost" size="sm" className="text-red-600 hover:bg-red-50" onClick={() => handleDelete(g.id)} data-testid={`delete-gasto-${g.id}`}>
                      <Trash size={16} />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      {/* Categories breakdown */}
      {resumen && resumen.por_categoria.length > 0 && (
        <Card className="p-5 mt-6 border-slate-200">
          <div className="flex items-center gap-2 mb-4">
            <ChartPieSlice size={20} weight="duotone" className="text-slate-500" />
            <h3 className="heading-font font-bold text-slate-900">{t("gastos.byCategory")} {resumen.anio}</h3>
          </div>
          <div className="space-y-2">
            {resumen.por_categoria.map((c) => {
              const pct = resumen.total > 0 ? (c.total / resumen.total) * 100 : 0;
              return (
                <div key={c.categoria}>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-slate-700 font-medium">{c.categoria}</span>
                    <span className="text-slate-900 font-semibold">€ {c.total.toFixed(2)} <span className="text-slate-400 text-xs">({pct.toFixed(1)}%)</span></span>
                  </div>
                  <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                    <div className="h-full bg-gradient-to-r from-green-500 to-red-500" style={{ width: `${pct}%` }} />
                  </div>
                </div>
              );
            })}
          </div>
        </Card>
      )}
    </div>
  );
}
